class StickyBuyButton extends HTMLElement {
  constructor() {
    super();

    this.activeClass = "wt-product__sticky-buy--show";
    this.bodyActiveClass = "aumpex-sticky-atc-visible";
    this.handleClick = this.handleClick.bind(this);
    this.handleIntersection = this.handleIntersection.bind(this);
    this.syncButtonState = this.syncButtonState.bind(this);
    this.updateStickyHeight = this.updateStickyHeight.bind(this);
  }

  connectedCallback() {
    this.initialize();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.buttonObserver?.disconnect();
    this.resizeObserver?.disconnect();
    window.removeEventListener("resize", this.updateStickyHeight);
    this.unsubscribeCartUpdate?.();
    this.unsubscribeCartError?.();
    this.unsubscribeVariantChange?.();
    document.body.classList.remove(this.bodyActiveClass);
  }

  initialize() {
    this.section = this.closest("section");
    this.addToCartModule = this.section?.querySelector(".wt-product__add-to-cart");
    this.originalButton = this.addToCartModule?.querySelector('[name="add"]');
    this.button = this.querySelector("button");
    this.buttonText = this.button?.querySelector("span");
    this.loader = this.querySelector(".loading-overlay__spinner");

    if (!this.addToCartModule || !this.originalButton || !this.button) return;

    this.button.addEventListener("click", this.handleClick);
    this.syncButtonState();
    this.updateStickyHeight();

    this.observer = new IntersectionObserver(this.handleIntersection, {
      threshold: 0,
    });
    this.observer.observe(this.addToCartModule);

    this.buttonObserver = new MutationObserver(this.syncButtonState);
    this.buttonObserver.observe(this.originalButton, {
      attributes: true,
      attributeFilter: ["disabled", "aria-disabled", "class"],
      childList: true,
      subtree: true,
    });

    this.resizeObserver = new ResizeObserver(this.updateStickyHeight);
    this.resizeObserver.observe(this);
    window.addEventListener("resize", this.updateStickyHeight);

    this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, () => {
      requestAnimationFrame(this.syncButtonState);
    });
    this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      this.syncButtonState();
    });
    this.unsubscribeCartError = subscribe(PUB_SUB_EVENTS.cartError, () => {
      this.syncButtonState();
    });
  }

  handleClick(event) {
    event.preventDefault();
    this.syncButtonState();
    if (this.button.disabled || this.button.getAttribute("aria-disabled") === "true") return;

    this.setLoading(true);
    this.originalButton.click();
  }

  handleIntersection(entries) {
    const entry = entries[0];
    const addToCartRect = this.addToCartModule.getBoundingClientRect();
    const shouldShow = !entry.isIntersecting && addToCartRect.bottom < 0;

    this.classList.toggle(this.activeClass, shouldShow);
    document.body.classList.toggle(this.bodyActiveClass, shouldShow);
    this.updateStickyHeight();
  }

  syncButtonState() {
    if (!this.originalButton || !this.button) return;

    const originalText = this.originalButton.querySelector("span")?.textContent?.trim();
    if (originalText && this.buttonText) this.buttonText.textContent = originalText;

    const disabled =
      this.originalButton.disabled ||
      this.originalButton.hasAttribute("disabled") ||
      this.originalButton.getAttribute("aria-disabled") === "true";

    this.button.disabled = disabled;
    this.button.toggleAttribute("disabled", disabled);
    this.button.setAttribute("aria-disabled", disabled ? "true" : "false");
    this.button.setAttribute("aria-label", originalText || this.button.getAttribute("aria-label"));
    this.setLoading(this.originalButton.classList.contains("loading"));
  }

  setLoading(isLoading) {
    this.button.classList.toggle("loading", isLoading);
    if (this.loader) this.loader.classList.toggle("hidden", !isLoading);
  }

  updateStickyHeight() {
    const height = Math.ceil(this.getBoundingClientRect().height || 72);
    document.body.style.setProperty("--aumpex-sticky-atc-height", `${height}px`);
  }
}

customElements.define("sticky-buy-button", StickyBuyButton);

if (!customElements.get("gallery-fashion")) {
  customElements.define(
    "gallery-fashion",
    class GalleryFashion extends HTMLElement {
      constructor() {
        super();
        this.section = this.closest("section");
        this.logoBanner = this;
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        this.removeEventsWhenDesignMode();
      }

      addEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.addEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.addEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      removeEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.removeEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.removeEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      reinitAfterDelay() {
        setTimeout(() => this.reinit(), 0);
      }

      isFirstSection() {
        const sectionWrapper = document.querySelector("#root");
        const firstSection = sectionWrapper.querySelector("section");
        this.section = this.closest("section");
        const currentSection = this.section;

        return firstSection === currentSection;
      }

      handleResize() {
        this.setTopMargin();
        this.positioningProductInfo();
      }

      observeHeader() {
        const header = document.querySelector(".wt-header");
        const activeTransparentClass = "wt-header--fashion-transparent";

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) {
                header.classList.remove(activeTransparentClass);
              } else {
                header.classList.add(activeTransparentClass);
              }
            });
          },
          { root: null, threshold: 0.05 },
        );

        observer.observe(this.querySelector(".wt-product__gallery"));
      }

      calculateOffset() {
        const header = document.querySelector("header");
        const headerHeight = header.offsetHeight;
        const offset =
          this.isTransparentHeaderEnabled() && this.isFirstSection()
            ? headerHeight
            : 0;

        return offset;
      }

      setTopMargin() {
        const offset = this.calculateOffset();
        if (offset) {
          this.section.style.marginTop = `-${offset}px`;
        } else {
          this.section.style.marginTop = 0;
        }
      }

      isTransparentHeaderEnabled() {
        const header = document.querySelector(".wt-header");
        return (
          header.dataset.transparent &&
          header.classList.contains("wt-header--v3")
        );
      }

      renderProgressBar() {
        const thumbsGallery = this.querySelector("[data-thumbs]");
        const progressBarElement = document.createElement("div");
        progressBarElement.classList.add("gallery-fashion__progress-bar");
        const progressBarIndicatorElement = document.createElement("div");
        progressBarIndicatorElement.classList.add(
          "gallery-fashion__progress-bar-indicator",
        );
        progressBarElement.appendChild(progressBarIndicatorElement);
        thumbsGallery.appendChild(progressBarElement);

        function updateProgressBar() {
          const images = this.querySelector("gallery-section");
          const progressBar = progressBarIndicatorElement;

          const scrolled = window.scrollY;
          const maxHeight = images.clientHeight - window.innerHeight;
          const scrollPercentage = (scrolled / maxHeight) * 100;

          const progressBarHeight = progressBarElement.clientHeight;
          const progressHeight = progressBar.clientHeight;
          const maxProgressTop = progressBarHeight - progressHeight;
          const progressTop = Math.min(
            (scrollPercentage / 100) * maxProgressTop,
            maxProgressTop,
          );

          progressBar.style.top = `${progressTop}px`;

          if (progressTop === maxProgressTop) {
            thumbsGallery.classList.add("finished");
          } else {
            thumbsGallery.classList.remove("finished");
          }
        }

        window.addEventListener("scroll", updateProgressBar.bind(this));

        function handleThumbsClick(event) {
          if (event.target.closest(".thumbs-list-item")) {
            const thumbnails = Array.from(
              event.currentTarget.querySelectorAll(".wt-product__img"),
            );
            const targetImage = event.target
              .closest(".thumbs-list-item")
              .querySelector(".wt-product__img");
            const index = thumbnails.indexOf(targetImage);
            const fullImage = this.querySelectorAll(
              ".wt-masonry__wrapper .wt-product__img, .wt-masonry__wrapper .wt-product__thumbnail-video",
            )[index];

            fullImage.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }

        thumbsGallery.addEventListener("click", handleThumbsClick.bind(this));
      }

      positioningProductInfo() {
        const productInfo = this.querySelector(".wt-product__main");
        const mediaDesktop = window.matchMedia("(min-width: 1200px)");

        if (mediaDesktop.matches) {
          productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 0)}px`;
        }

        const resizeObserver = new ResizeObserver((entries) => {
          for (let entry of entries) {
            if (mediaDesktop.matches) {
              productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 0)}px`;
            }
          }
        });

        const observedElement = productInfo;
        resizeObserver.observe(observedElement);
      }

      attachEvents() {
        window.addEventListener("resize", this.handleResize.bind(this));
      }

      /**
       * Wait until all images inside masonry wrapper are loaded
       * then run callback
       */
      waitForMasonryImages(callback) {
        const masonry = this.querySelector(".swiper-wrapper--masonry");
        if (!masonry) return;

        const images = masonry.querySelectorAll("img");
        if (images.length === 0) {
          callback();
          return;
        }

        let loaded = 0;
        const checkDone = () => {
          loaded++;
          if (loaded === images.length) {
            callback();
          }
        };

        images.forEach((img) => {
          if (img.complete) {
            checkDone();
          } else {
            img.addEventListener("load", checkDone, { once: true });
            img.addEventListener("error", checkDone, { once: true });
          }
        });
      }

      init() {
        this.reinitAfterDelay = this.reinitAfterDelay.bind(this);
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            this.waitForMasonryImages(() => {
              const masonry = this.querySelector(".swiper-wrapper--masonry");
              if (masonry) {
                stickyHeaderThreshold.style.height = `${masonry.offsetHeight}px`;
              }
            });
          }
          this.setTopMargin();
          this.observeHeader();
          this.attachEvents();
        }

        this.renderProgressBar();
        this.positioningProductInfo();
        this.addEventsWhenDesignMode();
      }

      reinit() {
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            stickyHeaderThreshold.style.height = `${this.querySelector(".swiper-wrapper--masonry").offsetHeight}px`;
          }
          this.setTopMargin();
          this.observeHeader();
        }
        this.positioningProductInfo();
      }
    },
  );
}
